import { useState, useMemo, useCallback, useEffect } from 'react'
import type { StratumHierarchy, DiagramScene, Stratum, SceneNode } from '../api/types'
import { titleCase } from '../utils/titleCase'
import styles from './Workspace.module.css'

/** Parse subtitle into info line and locations */
function parseSubtitle(text: string): { info: string | null; locations: string[] } {
    const lines = text.split('\n')
    const info = lines.find(l => !l.startsWith('at:')) ?? null
    const atLine = lines.find(l => l.startsWith('at:'))
    const locations = atLine ? atLine.replace('at:', '').split(',').map(s => s.trim()).filter(Boolean) : []
    return { info, locations }
}

/** Location pill matching graph canvas style */
function LocationPill({ name, color }: { name: string; color: string }) {
    return (
        <span style={{
            fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700,
            color, background: `color-mix(in srgb, ${color} 15%, transparent)`,
            padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap',
        }}>{name}</span>
    )
}

/** Get color for a node from scene data — uses the actual graph colors */
function nodeColorFromScene(nodeId: string, scene?: DiagramScene | null): string | undefined {
    if (!scene) return undefined
    const sn = scene.nodes.find(n => n.id === nodeId)
    return sn?.strokeColor
}

/** Header card for system or subsystem level */
function StratumHeader({ stratum, scene }: { stratum: Stratum; scene?: DiagramScene | null }) {
    const parsed = stratum.subtitle ? parseSubtitle(stratum.subtitle) : null
    const locs = parsed?.locations ?? []
    return (
        <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 16, background: 'var(--surface-elevated)', border: '1px solid var(--border-subtle)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ maxWidth: '50%' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{stratum.name || stratum.kind}</div>
                    {stratum.stats && scene && (
                        <div style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', marginTop: 2 }}>
                            {(() => {
                                const nodeIds = new Set(stratum.nodeIds ?? [])
                                const sceneNodes = scene.nodes.filter(sn => nodeIds.has(sn.id))
                                const roleCounts: Record<string, number> = {}
                                for (const sn of sceneNodes) {
                                    const r = sn.semanticRole ?? 'OTHER'
                                    roleCounts[r] = (roleCounts[r] ?? 0) + 1
                                }
                                const parts: string[] = []
                                const order = ['SOURCE', 'INTERMEDIATE', 'RESULT', 'HUB']
                                for (const r of order) {
                                    const c = roleCounts[r]
                                    if (c && c > 0) {
                                        const label = r === 'ACTION' ? 'action' : r === 'INTERMEDIATE' ? 'intermediate' : r.toLowerCase()
                                        parts.push(`${c} ${label}${c > 1 ? 's' : ''}`)
                                    }
                                }
                                return parts.join(' · ') || `${stratum.stats!.nodeCount} nodes`
                            })()}
                        </div>
                    )}
                </div>
                {locs.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end', alignContent: 'flex-start', maxWidth: '45%' }}>
                        {locs.map(loc => (
                            <LocationPill key={loc} name={loc} color={locationColorFromScene(loc, scene)} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

/** Get location color from scene location groups */
function locationColorFromScene(locationName: string, scene?: DiagramScene | null): string {
    if (!scene?.locationGroups) return 'var(--role-locus)'
    const lg = scene.locationGroups.find(g => g.label.toLowerCase() === locationName.toLowerCase())
    return lg?.strokeColor ?? 'var(--role-locus)'
}

/** Map structural role to a CSS variable color for left-bar accents */
function roleColor(role?: string | null): string | undefined {
    if (!role) return undefined
    switch (role.toUpperCase()) {
        case 'HUB': return 'var(--role-hub)'
        case 'SOURCE': return 'var(--role-source)'
        case 'SINK': case 'RESULT': return 'var(--role-result)'
        case 'FORK': case 'JOIN': return 'var(--accent)'
        case 'PIPELINE': case 'INTERMEDIATE': return 'var(--role-intermediate)'
        case 'CYCLE': return 'var(--accent)'
        case 'ACTION': return 'var(--role-action)'
        default: return undefined
    }
}

export interface StratumChangeInfo {
    stratumId: string | null
    structuralKey: string | null
    nodeIds: string[]
    name: string
    isAutoInlined?: boolean
}

interface StratumNavigatorProps {
    hierarchy: StratumHierarchy
    scene: DiagramScene | null
    onStratumChange?: (info: StratumChangeInfo) => void
    onStepFocus?: (nodeId: string) => void
    focusStack: string[]
    onPushFocus: (id: string) => void
}

function buildStructuralKey(stratum: Stratum): string {
    const depthPart = stratum.depth != null ? `${stratum.depth}` : '?'
    const role = stratum.structuralRole ?? 'NONE'
    return `${stratum.kind}:${depthPart}:${role}`
}

function stratumChangeInfo(stratum: Stratum | null, root?: Stratum | null, isAutoInlined?: boolean): StratumChangeInfo {
    if (stratum) {
        return {
            stratumId: stratum.id,
            structuralKey: buildStructuralKey(stratum),
            nodeIds: [...(stratum.nodeIds ?? [])],
            name: stratum.name || stratum.id,
            isAutoInlined,
        }
    }
    return {
        stratumId: null,
        structuralKey: null,
        nodeIds: [...(root?.nodeIds ?? [])],
        name: root?.name ?? 'System',
    }
}

export function StratumNavigator({
    hierarchy, scene,
    onStratumChange, onStepFocus,
    focusStack, onPushFocus,
}: StratumNavigatorProps) {
    const root = hierarchy?.strata?.[hierarchy?.rootId]
    const focusedId = focusStack.length > 0 ? focusStack[focusStack.length - 1] : null
    const stackKey = focusedId ?? 'root'

    // Track all visited levels so we keep their DOM alive (preserves scroll natively)
    const [visitedKeys, setVisitedKeys] = useState<string[]>(['root'])
    useEffect(() => {
        setVisitedKeys(prev => prev.includes(stackKey) ? prev : [...prev, stackKey])
    }, [stackKey])
    // Prune levels no longer in the stack (navigated back past them)
    const activeKeys = useMemo(() => {
        const validKeys = new Set(['root', ...focusStack])
        return visitedKeys.filter(k => validKeys.has(k))
    }, [visitedKeys, focusStack])

    const inlineStage = useMemo(() => {
        if (!root) return null
        const stages = (root.childIds ?? [])
            .map(id => hierarchy?.strata?.[id])
            .filter((s): s is Stratum => s != null && s.kind === 'STAGE')
        if (stages.length === 1) return stages[0]
        return null
    }, [root, hierarchy?.strata])

    useEffect(() => {
        const r = hierarchy?.strata?.[hierarchy?.rootId]
        if (inlineStage) {
            onStratumChange?.(stratumChangeInfo(inlineStage, undefined, true))
        } else {
            if (focusStack.length === 0) {
                onStratumChange?.(stratumChangeInfo(null, r))
            }
        }
    }, [hierarchy?.rootId, inlineStage, focusStack.length])

    const pushFocus = useCallback((id: string) => {
        onPushFocus(id)
        const stratum = hierarchy?.strata?.[id]
        if (stratum) {
            onStratumChange?.(stratumChangeInfo(stratum))
            if (stratum.kind === 'NODE') {
                const sn = getSceneNode(stratum, scene)
                if (sn) onStepFocus?.(sn.id)
            }
        }
    }, [hierarchy?.strata, onStratumChange, onStepFocus, scene, onPushFocus])

    return (
        <div className={styles.workspace} style={{ position: 'relative' }}>
            {/* Each visited level is its own scroll container.
                Non-current levels stay in DOM (hidden) — browser preserves scroll natively. */}
            {activeKeys.map(levelKey => {
                const isCurrent = levelKey === stackKey
                return (
                    <div
                        key={levelKey}
                        className={styles.wsBody}
                        style={isCurrent ? undefined : { position: 'absolute', inset: 0, visibility: 'hidden', pointerEvents: 'none' }}
                    >
                        <LevelContent
                            levelKey={levelKey}
                            hierarchy={hierarchy}
                            scene={scene}
                            root={root ?? null}
                            inlineStage={inlineStage}
                            pushFocus={pushFocus}
                        />
                    </div>
                )
            })}
        </div>
    )
}

/** Hook: compute steps for a given navigation level */
function useLevelSteps(
    current: Stratum | null | undefined,
    hierarchy: StratumHierarchy,
    scene: DiagramScene | null,
    inlineStage: Stratum | null,
    root: Stratum | null,
) {
    return useMemo(() => {
        if (!current) return []

        // Get children (next level down)
        let children = (current.childIds ?? [])
            .map(id => hierarchy.strata[id])
            .filter((s): s is Stratum => s != null)

        // Flatten stages → show nodes directly, grouped by stage with separators
        const shouldFlatten =
            (current.kind === 'SYSTEM' && !children.some(c => c.kind === 'SUBSYSTEM')) ||
            (current.kind === 'SUBSYSTEM')
        if (shouldFlatten && scene) {
            const stages = children
                .filter(c => c.kind === 'STAGE')
                .sort((a, b) => (a.depth ?? 0) - (b.depth ?? 0) || (a.spectralCoord ?? 0) - (b.spectralCoord ?? 0))
            if (stages.length > 0) {
                type FlatStep = {
                    id: string; kind: 'NODE' | 'SEPARATOR'; name: string;
                    role: string | null; description?: string; selfRole: string | null; meta: string;
                    actor?: string; location?: string; locations?: string[];
                    drillable: boolean;
                }
                // Build entity → locations map from scene edges
                const entityLocations = new Map<string, Set<string>>()
                const entityToActionLocs = new Map<string, Set<string>>()
                for (const edge of scene.edges) {
                    const src = scene.nodes.find((n: any) => n.id === edge.sourceId)
                    const tgt = scene.nodes.find((n: any) => n.id === edge.targetId)
                    if (src?.semanticRole === 'ACTION' && src.locationLabel && tgt && tgt.semanticRole !== 'ACTION') {
                        if (!entityToActionLocs.has(tgt.id)) entityToActionLocs.set(tgt.id, new Set())
                        entityToActionLocs.get(tgt.id)!.add(src.locationLabel)
                    }
                    if (tgt?.semanticRole === 'ACTION' && tgt.locationLabel && src && src.semanticRole !== 'ACTION') {
                        if (!entityToActionLocs.has(src.id)) entityToActionLocs.set(src.id, new Set())
                        entityToActionLocs.get(src.id)!.add(tgt.locationLabel)
                    }
                }
                for (const [eid, locs] of entityToActionLocs) {
                    entityLocations.set(eid, locs)
                }

                const flat: FlatStep[] = []
                stages.forEach((stage, si) => {
                    if (si > 0) {
                        flat.push({
                            id: `sep:${stage.id}`, kind: 'SEPARATOR' as const,
                            name: '', role: null, description: undefined, selfRole: null, meta: '',
                            drillable: false,
                        })
                    }
                    const stageNodeIds = new Set(stage.nodeIds ?? [])
                    scene.nodes
                        .filter(sn => stageNodeIds.has(sn.id))
                        .forEach(sn => {
                            const metaParts: string[] = []
                            if (sn.actorLabel) metaParts.push(`by ${titleCase(sn.actorLabel)}`)
                            if (sn.locationLabel) metaParts.push(`at ${titleCase(sn.locationLabel)}`)
                            const locs = sn.locationLabel
                                ? [sn.locationLabel]
                                : [...(entityLocations.get(sn.id) ?? [])]
                            flat.push({
                                id: sn.id, kind: 'NODE' as const,
                                name: titleCase(sn.displayName),
                                role: sn.semanticRole ?? null,
                                description: undefined,
                                selfRole: null, meta: metaParts.join(' \u00b7 '),
                                actor: sn.actorLabel ?? undefined,
                                location: locs.length === 1 ? locs[0] : undefined,
                                locations: locs.length > 1 ? locs : undefined,
                                drillable: false,
                            })
                        })
                })
                return flat
            }
        }

        // Filter out "self" node if at stage level
        if (current.kind === 'STAGE') {
            const stageName = (current.name || '').toLowerCase()
            children = children.filter(c => (c.name || '').toLowerCase() !== stageName)
        }

        // Sort children
        children.sort((a, b) =>
            (a.depth ?? 0) - (b.depth ?? 0) ||
            (a.spectralCoord ?? 0) - (b.spectralCoord ?? 0))

        // Scene node fallback: if no stratum children, build steps from scene nodes
        // scoped to this stratum's nodeIds (or all nodes at root level)
        if (children.length === 0 && scene && scene.nodes.length > 0) {
            const sourceNodeIds = current.nodeIds ?? []
            const ids = sourceNodeIds.length > 0 ? new Set(sourceNodeIds) : null
            const sceneNodes = ids ? scene.nodes.filter(sn => ids.has(sn.id)) : scene.nodes

            const sceneSteps = sceneNodes.map(sn => {
                const metaParts: string[] = []
                if (sn.actorLabel) metaParts.push(`by ${titleCase(sn.actorLabel)}`)
                if (sn.locationLabel) metaParts.push(`at ${titleCase(sn.locationLabel)}`)

                return {
                    id: sn.id,
                    kind: 'NODE' as const,
                    name: titleCase(sn.displayName),
                    role: sn.semanticRole ?? null,
                    description: undefined as string | undefined,
                    selfRole: null as string | null,
                    meta: metaParts.join(' \u00b7 '),
                    actor: sn.actorLabel ?? undefined,
                    location: sn.locationLabel ?? undefined,
                    locations: undefined as string[] | undefined,
                    drillable: false,
                }
            })

            return sceneSteps
        }

        // Build child steps
        const resultSteps = children.map(child => {
            let meta = ''
            let actor: string | undefined
            let location: string | undefined
            let selfRole: string | null = null

            if (child.kind === 'NODE') {
                const sn = getSceneNode(child, scene)
                if (sn) {
                    actor = sn.actorLabel ?? undefined
                    location = sn.locationLabel ?? undefined
                    const parts: string[] = []
                    if (sn.actorLabel) parts.push(`by ${titleCase(sn.actorLabel)}`)
                    if (sn.locationLabel) parts.push(`at ${titleCase(sn.locationLabel)}`)
                    meta = parts.join(' \u00b7 ')
                }
            } else {
                // For STAGE/SUBSYSTEM: resolve self-node's semantic role for color
                if (scene) {
                    const childName = (child.name || '').toLowerCase()
                    const selfNodeStratum = (child.childIds ?? [])
                        .map(id => hierarchy.strata[id])
                        .find(s => s && s.kind === 'NODE' && (s.name || '').toLowerCase() === childName)
                    if (selfNodeStratum) {
                        const sn = getSceneNode(selfNodeStratum, scene)
                        if (sn?.semanticRole) selfRole = sn.semanticRole
                    }
                }
                const s = child.stats
                if (s) {
                    const parts: string[] = []
                    if (s.nodeCount != null) parts.push(`${s.nodeCount} node${s.nodeCount !== 1 ? 's' : ''}`)
                    meta = parts.join(' \u00b7 ')
                }
            }

            return {
                id: child.id,
                kind: child.kind,
                name: titleCase(child.name || child.id),
                role: child.structuralRole,
                description: child.description as string | undefined,
                subtitle: child.subtitle as string | undefined,
                selfRole,
                meta,
                actor,
                location,
                locations: undefined as string[] | undefined,
                drillable: child.kind !== 'NODE' && (child.stats?.nodeCount ?? 0) > 1,
            }
        })

        return resultSteps
    }, [current, inlineStage, root, hierarchy, scene])
}

/** Renders the content for a single navigation level */
function LevelContent({
    levelKey, hierarchy, scene, root, inlineStage, pushFocus,
}: {
    levelKey: string
    hierarchy: StratumHierarchy
    scene: DiagramScene | null
    root: Stratum | null
    inlineStage: Stratum | null
    pushFocus: (id: string) => void
}) {
    const focused = levelKey !== 'root' ? hierarchy?.strata?.[levelKey] ?? null : null
    const current = focused || (inlineStage ? inlineStage : root)

    const steps = useLevelSteps(current, hierarchy, scene, inlineStage, root)

    return (
        <>
                <div style={{ marginTop: 8, paddingLeft: 4 }}>
                    {current && (current.kind === 'SYSTEM' || current.kind === 'SUBSYSTEM') && (
                        <StratumHeader stratum={current} scene={scene} />
                    )}
                    {steps.map((step) => {
                        if (step.kind === 'SEPARATOR') {
                            return <div key={step.id} style={{ height: 20 }} />
                        }

                        const rc = nodeColorFromScene(step.id, scene) ?? roleColor(step.role) ?? roleColor(step.selfRole) ?? 'var(--accent)'
                        const dotStyle = { borderColor: rc, background: `color-mix(in srgb, ${rc} 25%, var(--surface))` }

                        // Build relationship description from role counts
                        const stratum = hierarchy.strata[step.id]
                        const relationshipParts: string[] = []
                        if (stratum && scene) {
                            const nodeIds = new Set(stratum.nodeIds ?? [])
                            const sceneNodes = scene.nodes.filter(sn => nodeIds.has(sn.id))
                            const roleCounts: Record<string, number> = {}
                            for (const sn of sceneNodes) {
                                const r = sn.semanticRole ?? 'OTHER'
                                roleCounts[r] = (roleCounts[r] ?? 0) + 1
                            }
                            const order = ['ACTION', 'SOURCE', 'INTERMEDIATE', 'RESULT', 'HUB']
                            for (const r of order) {
                                const c = roleCounts[r]
                                if (c && c > 0) {
                                    const label = r === 'ACTION' ? 'action' : r === 'INTERMEDIATE' ? 'intermediate' : r.toLowerCase()
                                    relationshipParts.push(`${c} ${label}${c > 1 ? 's' : ''}`)
                                }
                            }
                        }

                        const relationship = relationshipParts.join(' · ')

                        const metaContent = relationship ? (
                            <div className={styles.stepMeta}>
                                <span>{relationship}</span>
                            </div>
                        ) : null

                        return (
                            <div key={step.id} style={{ position: 'relative', paddingLeft: 32, marginBottom: 6 }}>
                                <div className={styles.stepLine} />
                                <div className={styles.stepDot} style={dotStyle} />
                                <div
                                    style={{
                                        background: 'var(--surface-elevated)',
                                        border: '1px solid var(--border-subtle)',
                                        borderRadius: 16,
                                        padding: '12px 16px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    }}
                                >
                                    {(() => {
                                        const parsed = ('subtitle' in step && step.subtitle) ? parseSubtitle(step.subtitle) : null
                                        const locs = parsed?.locations ?? []


                                        const content = (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, width: '100%' }}>
                                                <div className={styles.stepMain} style={{ maxWidth: '50%' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2, flexWrap: 'wrap' }}>
                                                        {step.actor ? (
                                                            <span style={{
                                                                fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700, color: rc,
                                                            }}>{step.actor.toLowerCase()}</span>
                                                        ) : step.role ? (
                                                            <span style={{ fontSize: 9, fontWeight: 700, color: rc, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                                                {step.role}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    <div className={styles.stepTitle}>{step.name}</div>
                                                    {metaContent}
                                                </div>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignContent: 'flex-start', gap: 3, maxWidth: '45%' }}>
                                                    {step.location && (
                                                        <LocationPill name={step.location} color={locationColorFromScene(step.location, scene)} />
                                                    )}
                                                    {step.locations?.map((loc: string) => (
                                                        <LocationPill key={loc} name={loc} color={locationColorFromScene(loc, scene)} />
                                                    ))}
                                                    {locs.length > 0 && locs.map(loc => (
                                                        <LocationPill key={loc} name={loc} color={locationColorFromScene(loc, scene)} />
                                                    ))}
                                                </div>
                                            </div>
                                        )

                                        return step.drillable ? (
                                            <button className={styles.stepHeader} onClick={() => pushFocus(step.id)}>
                                                {content}
                                                <span className={styles.stepChevron}>{'\u203A'}</span>
                                            </button>
                                        ) : (
                                            <div className={styles.stepHeader}>
                                                {content}
                                            </div>
                                        )
                                    })()}
                                </div>
                            </div>
                        )
                    })}
                </div>

                <div aria-hidden style={{ height: 80, flexShrink: 0 }} />
        </>
    )
}

function getSceneNode(s: Stratum, scene: DiagramScene | null): SceneNode | null {
    if (!scene) return null
    const nodeIds = s.nodeIds ?? []
    const nodeId = (Array.isArray(nodeIds) ? nodeIds[0] : [...nodeIds][0]) ?? s.id.replace('node:', '')
    return scene.nodes?.find(n => n.id === nodeId) ?? null
}
